import { useCallback, useLayoutEffect, useRef, useState, useTransition, type ReactNode } from 'react';
import { Router } from 'react-router-dom';
import {
  createBrowserHistory,
  type Action,
  type BrowserHistory,
  type Location,
} from '@remix-run/router';
import { RoutePendingContext } from './routePendingContext';

/**
 * The react-router-dom release this file mirrors.
 *
 * It is a FORK of a private implementation, so `react-router-dom` and
 * `@remix-run/router` are pinned to exact versions in `package.json` rather than
 * carets. Moving either pin means re-diffing the body of this component against
 * the new `BrowserRouter` FIRST — the public types are stable across the 6.x
 * line even where the runtime behaviour is not, so nothing else would catch a
 * divergence. `__tests__/TransitionRouter.version.test.ts` fails if the pin
 * moves without this constant moving with it.
 */
export const MIRRORED_REACT_ROUTER_VERSION = '6.30.4';

/**
 * `BrowserRouter` with its pending state made visible.
 *
 * This is react-router 6.30's own `BrowserRouter` (`react-router-dom/dist/index.js`)
 * with exactly one thing changed: where it wraps its `setState` in a bare
 * `React.startTransition` when `future.v7_startTransition` is set, this wraps it
 * in `useTransition()` — the same transition, plus the `isPending` flag React
 * hands back. Navigation semantics are therefore unchanged: an in-app navigation
 * to a not-yet-loaded route chunk still keeps the previous page on screen instead
 * of flashing `PageSkeleton`, which is the behaviour `v7_startTransition` exists
 * to produce and which we deliberately keep.
 *
 * We own the router rather than reading a pending flag off react-router because
 * there is nothing to read: `BrowserRouter` keeps its transition private, and
 * `useNavigation()` — the hook that would answer this — is data-router-only
 * (`createBrowserRouter`), which this app does not use. A component INSIDE
 * `<Routes>` cannot derive it either: a suspended transition blocks that whole
 * update, so `useLocation()` does not advance until the destination commits.
 *
 * `basename` and `window` are omitted from `BrowserRouter`'s prop set because the
 * app passes neither; add them here rather than at a call site if that changes.
 */
export function TransitionRouter({ children }: { children: ReactNode }) {
  const historyRef = useRef<BrowserHistory>();
  if (historyRef.current == null) {
    // `v5Compat: true` matches BrowserRouter: it is what makes `listen` fire on
    // a push/replace, not just on a pop.
    historyRef.current = createBrowserHistory({ v5Compat: true });
  }
  const history = historyRef.current;

  const [state, setState] = useState({ action: history.action, location: history.location });
  const [isPending, startTransition] = useTransition();

  const setStateInTransition = useCallback(
    // `history.listen` passes an `Update`, which the package's index does not
    // re-export; the two fields the router state needs are declared instead.
    (update: { action: Action; location: Location }) => {
      startTransition(() => setState({ action: update.action, location: update.location }));
    },
    [startTransition]
  );

  useLayoutEffect(() => history.listen(setStateInTransition), [history, setStateInTransition]);

  return (
    <RoutePendingContext.Provider value={isPending}>
      <Router
        location={state.location}
        navigationType={state.action}
        navigator={history}
        future={{ v7_relativeSplatPath: true }}
      >
        {children}
      </Router>
    </RoutePendingContext.Provider>
  );
}
